import React, { type ReactNode } from 'react';
import Tooltip, {
  TOOLTIP_ALIGN_CENTER,
  TOOLTIP_PLACEMENT_BOTTOM,
  TOOLTIP_SIZE_SMALL,
  type TooltipAlign,
  type TooltipPlacement,
  type TooltipSize,
} from '../Tooltip';
import { InfoButton } from './PageInfo.styled';

export interface PageInfoProps {
  /** The body shown inside the tooltip — typically a sentence describing the page. */
  text: ReactNode;
  /** Override the default Material `info` glyph (e.g. to swap in a question mark). */
  icon?: ReactNode;
  /** Override the icon button's aria-label (defaults to "About this page"). */
  ariaLabel?: string;
  placement?: TooltipPlacement;
  align?: TooltipAlign;
  size?: TooltipSize;
  /** Render the tip outside clipping ancestors and keep it inside the viewport. */
  portal?: boolean;
  className?: string;
  dataTestId?: string;
}

const DefaultInfoIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <line x1="12" y1="11" x2="12" y2="17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

const PageInfo: React.FC<PageInfoProps> = ({
  text,
  icon,
  ariaLabel = 'About this page',
  placement = TOOLTIP_PLACEMENT_BOTTOM,
  align = TOOLTIP_ALIGN_CENTER,
  size = TOOLTIP_SIZE_SMALL,
  portal = false,
  className,
  dataTestId = 'page-info',
}) => (
  <Tooltip
    content={text}
    placement={placement}
    align={align}
    size={size}
    portal={portal}
    className={className}
    dataTestId={`${dataTestId}__tooltip`}
  >
    <InfoButton
      type="button"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {icon ?? <DefaultInfoIcon />}
    </InfoButton>
  </Tooltip>
);

export default PageInfo;
